import { css } from '@emotion/react';
import { useNavigate } from 'react-router';
import {
  SelectInput,
  TextInput,
  Text,
  colors,
  Form,
  FormElements,
  Divider,
  DatePicker,
  Button,
  Table,
  useDisclosure,
} from 'common-components';
import { TSociodemographicCriteriaValues } from '@definitions';
import { useSociodemographicResources, useAppTranslation } from '@hooks';
import { UserDataType } from '@pages/types';
import { DeleteModal } from '@components/modal';
import { VerticalFormItem } from '@components/display';
import { PropsWithChildren, useEffect } from 'react';
import { DefaultOptionType } from 'antd/es/select';

import { store } from '@/store';

type InformationContributorPropsType = {
  userContributor: UserDataType
};

const ProfileSection = ({
  title,
  description,
  children,
}: PropsWithChildren<{ title: string; description: string }>) => (
  <div
    css={css`
      display: flex;
      gap: 32px;
    `}
  >
    <div
      css={css`
        flex: 0 0 280px;
        display: flex;
        flex-direction: column;
      `}
    >
      <Text color={colors.gray700} style={{ fontWeight: 500 }}>
        {title}
      </Text>
      <Text color={colors.gray600}>{description}</Text>
    </div>
    <div
      css={css`
        flex: 1;
        min-width: 0;
      `}
    >
      {children}
    </div>
  </div>
);

const fieldsGrid = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const InformationContributor = ({ userContributor }: InformationContributorPropsType) => {
  const updateContributorAction = store.use.updateContributorAction().call;
  const deleteContributorsAction = store.use.deleteContributorsAction().call;

  const isLoadingUpdateContributorAction = store.use.updateContributorAction().status === 'LOADING';
  const isLoadingDeleteContributorsAction =
    store.use.deleteContributorsAction().status === 'LOADING';

  const [form] = FormElements.useForm<UserDataType>();
  const blacklistedProjects = FormElements.useWatch('blacklistedProject', { form, preserve: true });
  const { translate: t } = useAppTranslation();
  const navigate = useNavigate();
  const { open, isOpen, close } = useDisclosure();

  const { getResourceOptions } = useSociodemographicResources();

  useEffect(() => {
    Object.keys(userContributor).map((key: keyof UserDataType) => {
      form.setFieldValue(key, userContributor[key]);
    });
  }, [JSON.stringify(userContributor)]);

  const onUpdateConfirm = async () => {
    const values = form.getFieldsValue();

    updateContributorAction({
      idUser: values.idUser,
      phone: values.phone,
      lastName: values.lastName,
      firstName: values.firstName,
      birthDate: values.birthDate,
      nationality: values.nationality,
      earning: values.earning,
      study: values.study,
      family: values.family,
      children: values.children,
      job: values.job,
      activitySector: values.activitySector,
      address: values.address,
    });
  };

  const deleteUserHandler = async () => {
    deleteContributorsAction(
      {
        idContributors: [form.getFieldValue('idUser')],
      },
      () => {
        navigate(-1);
      },
    );
  };
  const getSelectInputPropsByName = (
    name: Parameters<typeof form.setFieldValue>[0],
    resourceType: TSociodemographicCriteriaValues,
  ) => {
    return {
      // @ts-ignore
      onChange: (_, option: DefaultOptionType | DefaultOptionType[]) => form.setFieldValue(name, option),
      options: getResourceOptions(resourceType),
    };
  };

  const blacklistColumns = [
    {
      title: t('contributor.profile.blacklist.project'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('contributor.profile.blacklist.tasks'),
      dataIndex: 'tasks',
      key: 'tasks',
    },
    {
      title: t('contributor.profile.blacklist.averageQuality'),
      dataIndex: 'averageQuality',
      key: 'averageQuality',
    },
    {
      title: t('project.taskline.table.origin'),
      dataIndex: 'origin',
      key: 'origin',
    },
    {
      title: t('contributor.profile.blacklist.exclusionDate'),
      dataIndex: 'exclusionDate',
      key: 'exclusionDate',
    },
    {
      title: t('managers.actions'),
      dataIndex: 'actions',
      key: 'actions',
    },
  ];

  const blacklistData = blacklistedProjects?.map(() => {
    return {
      key: 'NO INFO FROM BE',
      name: 'NO INFO FROM BE',
      projectImg: 'NO INFO FROM BE',
      tasks: 'NO INFO FROM BE',
      averageQuality: 'NO INFO FROM BE',
      origin: 'NO INFO FROM BE',
      exclusionDate: 'NO INFO FROM BE',
      actions: 'NO INFO FROM BE',
    };
  });

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 24px;
        .ant-divider {
          margin: 0;
          border-color: ${colors.gray200};
        }
      `}
    >
      <Form form={form}>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 24px;
          `}
        >
          <ProfileSection
            title={t('contributor.profile.sections.general')}
            description={t('contributor.profile.description')}
          >
            <div css={fieldsGrid}>
              <VerticalFormItem
                name={'idUser'}
                label={t('contributor.profile.fields.id')}
                style={{ gridColumn: 'span 2' }}
              >
                <TextInput disabled />
              </VerticalFormItem>
              <VerticalFormItem name={'email'} label={t('createUser.email.label')}>
                <TextInput disabled />
              </VerticalFormItem>
              <VerticalFormItem name={'phone'} label={t('createUser.phone.label')}>
                <TextInput />
              </VerticalFormItem>
              <VerticalFormItem name={'lastName'} label={t('createUser.lastName.label')}>
                <TextInput />
              </VerticalFormItem>
              <VerticalFormItem name={'firstName'} label={t('createUser.firstName.label')}>
                <TextInput />
              </VerticalFormItem>
              <VerticalFormItem
                label={t('contributor.profile.fields.birthDate')}
                name={'birthDate'}
                getValueFromEvent={(date) => date ?? null}
              >
                <DatePicker allowClear style={{ width: '100%' }} />
              </VerticalFormItem>
              <VerticalFormItem
                name={'nationality'}
                label={t('contributor.profile.fields.nationality')}
              >
                <SelectInput {...getSelectInputPropsByName('nationality', 'country')} />
              </VerticalFormItem>
            </div>
          </ProfileSection>
          <Divider />
          <ProfileSection
            title={t('contributor.profile.sections.socioDemographic')}
            description={t('contributor.profile.description')}
          >
            <div css={fieldsGrid}>
              <VerticalFormItem name={'earning'} label={t('contributor.profile.fields.earning')}>
                <SelectInput {...getSelectInputPropsByName('earning', 'earnings')} />
              </VerticalFormItem>
              <VerticalFormItem name={'study'} label={t('contributor.profile.fields.study')}>
                <SelectInput {...getSelectInputPropsByName('study', 'education')} />
              </VerticalFormItem>
              <VerticalFormItem name={'family'} label={t('contributor.profile.fields.family')}>
                <SelectInput {...getSelectInputPropsByName('family', 'maritalStatus')} />
              </VerticalFormItem>
              <VerticalFormItem name={'children'} label={t('contributor.profile.fields.children')}>
                <SelectInput {...getSelectInputPropsByName('children', 'children')} />
              </VerticalFormItem>
              <VerticalFormItem name={'job'} label={t('contributor.profile.fields.job')}>
                <SelectInput {...getSelectInputPropsByName('job', 'profession')} />
              </VerticalFormItem>
              <VerticalFormItem
                name={'activitySector'}
                label={t('contributor.profile.fields.activitySector')}
              >
                <SelectInput {...getSelectInputPropsByName('activitySector', 'activity')} />
              </VerticalFormItem>
            </div>
          </ProfileSection>
          <Divider />
          <ProfileSection
            title={t('contributor.profile.sections.address')}
            description={t('contributor.profile.description')}
          >
            <div css={fieldsGrid}>
              <VerticalFormItem
                name={['address', 'path']}
                label={t('contributor.profile.fields.address')}
                style={{ gridColumn: 'span 2' }}
              >
                <TextInput />
              </VerticalFormItem>
              <VerticalFormItem
                name={['address', 'zipCode']}
                label={t('contributor.profile.fields.zipCode')}
              >
                <TextInput />
              </VerticalFormItem>
              <VerticalFormItem name={['address', 'city']} label={t('contributor.profile.fields.city')}>
                <TextInput />
              </VerticalFormItem>
              <VerticalFormItem
                name={['address', 'region']}
                label={t('contributor.profile.fields.region')}
              >
                <SelectInput {...getSelectInputPropsByName(['address', 'region'], 'region')} />
              </VerticalFormItem>
              <VerticalFormItem name={['address', 'country']} label={t('createSegment.country')}>
                <SelectInput {...getSelectInputPropsByName(['address', 'country'], 'country')} />
              </VerticalFormItem>
            </div>
          </ProfileSection>
        </div>
      </Form>
      <Divider />
      <ProfileSection
        title={t('contributor.profile.blacklist.title')}
        description={t('contributor.profile.description')}
      >
        <Table data={blacklistData} columns={blacklistColumns} />
      </ProfileSection>
      <Button
        type="primary"
        size="sm"
        style={{ alignSelf: 'flex-end' }}
        loading={isLoadingUpdateContributorAction}
        disabled={isLoadingUpdateContributorAction}
        onClick={onUpdateConfirm}
      >
        {t('project.settings.buttons.saveChanges')}
      </Button>
      <div
        css={css`
          border: 1px solid ${colors.gray200};
          padding: 24px;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
        `}
      >
        <Text color={colors.gray900} style={{ fontSize: 16, lineHeight: '24px', fontWeight: 500 }}>
          {t('contributor.delete.title')}
        </Text>
        <Button onClick={() => open()} type="dangerFilled" size="sm" iconLeft="Trash01">
          {t('contributor.delete.button')}
        </Button>
        <DeleteModal
          close={close}
          isOpen={isOpen}
          isLoading={isLoadingDeleteContributorsAction}
          title={t('contributor.delete.modal.title')}
          label={t('contributor.delete.modal.label')}
          onDeleteMutation={deleteUserHandler}
        />
      </div>
    </div>
  );
};

export default InformationContributor;
